import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TableSortLabel,
  Toolbar,
  Typography,
  TextField,
} from "@mui/material";

const sampleData = [
  { id: 10, name: "John Doe", email: "john.doe@email.com", age: 28, department: "Engineering", salary: 75000 },
  { id: 20, name: "Jane Smith", email: "jane.smith@email.com", age: 32, department: "Marketing", salary: 68000 },
  { id: 30, name: "Mike Johnson", email: "mike.johnson@email.com", age: 25, department: "Engineering", salary: 72000 },
  { id: 40, name: "Sarah Williams", email: "sarah.williams@email.com", age: 29, department: "Sales", salary: 65000 },
  { id: 50, name: "David Brown", email: "david.brown@email.com", age: 35, department: "Engineering", salary: 85000 },
  { id: 60, name: "Lisa Davis", email: "lisa.davis@email.com", age: 27, department: "Marketing", salary: 63000 },
  { id: 70, name: "Tom Wilson", email: "tom.wilson@email.com", age: 31, department: "Sales", salary: 70000 },
  { id: 80, name: "Amy Taylor", email: "amy.taylor@email.com", age: 26, department: "HR", salary: 58000 },
];

const columns = [
  { id: "name", label: "Name", sortable: true },
  { id: "email", label: "Email", sortable: true },
  { id: "age", label: "Age", sortable: true, numeric: true },
  { id: "department", label: "Department", sortable: true },
  {
    id: "salary",
    label: "Salary",
    sortable: true,
    numeric: true,
    format: (value) => `$${value.toLocaleString()}`,
  },
];

export default function SortableSearchableTable({
  data = sampleData,
  tableColumns = columns,
  onSelectionChange,
  initialSelected = [],   // <-- nyt prop
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Sæt initialSelected når komponenten mountes
  useEffect(() => {
    if (initialSelected.length > 0) {
      const initialSet = new Set(initialSelected);
      setSelectedRows(initialSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // <-- tom dependency array = kun ved mount

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) =>
    order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);

  const handleSelectRow = (rowId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    setSelectedRows(newSelected);
    console.log("Selected rows:", Array.from(newSelected));
    if (onSelectionChange) {
      const selectedData = data.filter((row) => newSelected.has(row.id));
      onSelectionChange(Array.from(newSelected), selectedData);
    }
  };

  const handleSelectAll = (filteredData) => {
    const allSelected = filteredData.every((row) => selectedRows.has(row.id));
    const newSelected = new Set(selectedRows);

    if (allSelected) {
      filteredData.forEach((row) => newSelected.delete(row.id));
    } else {
      filteredData.forEach((row) => newSelected.add(row.id));
    }
    setSelectedRows(newSelected);

    if (onSelectionChange) {
      const selectedData = data.filter((row) => newSelected.has(row.id));
      onSelectionChange(Array.from(newSelected), selectedData);
    }
  };

  const filteredAndSortedData = useMemo(() => {
    let filteredData = data;
    if (searchTerm) {
      filteredData = data.filter((row) =>
        Object.values(row).some((value) =>
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    if (orderBy) {
      filteredData = filteredData.slice().sort(getComparator(order, orderBy));
    }
    return filteredData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchTerm, order, orderBy]);

  const allSelected =
    filteredAndSortedData.length > 0 &&
    filteredAndSortedData.every((row) => selectedRows.has(row.id));

  const indeterminate =
    filteredAndSortedData.some((row) => selectedRows.has(row.id)) &&
    !allSelected;

  return (
    <>
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        Vælg hvilke sensorer, der skal eksporteres fra.
      </Typography>

      <Toolbar>
        <TextField
          size="small"
          label="Søg..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ maxWidth: 300 }}
        />
        {selectedRows.size > 0 && (
          <Typography sx={{ ml: 2 }} color="primary">
            {selectedRows.size} selected
          </Typography>
        )}
      </Toolbar>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={indeterminate}
                  checked={allSelected}
                  onChange={() => handleSelectAll(filteredAndSortedData)}
                />
              </TableCell>
              {tableColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.numeric ? "right" : "left"}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedData.length > 0 ? (
              filteredAndSortedData.map((row, index) => {
                const selected = selectedRows.has(row.id);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={selected}
                    onClick={() => handleSelectRow(row.id)}   // <-- klik på rækken vælger
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onClick={(e) => e.stopPropagation()} // <-- så checkbox ikke trigges 2 gange
                        onChange={() => handleSelectRow(row.id)}
                      />
                    </TableCell>
                    {tableColumns.map((column) => (
                      <TableCell key={column.id} align={column.numeric ? "right" : "left"}>
                        {column.format ? column.format(row[column.id]) : row[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={tableColumns.length + 1} align="center">
                  <Typography color="textSecondary">No results found</Typography>
                  {searchTerm && (
                    <Typography variant="body2" color="textSecondary">
                      for "{searchTerm}"
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2} display="flex" justifyContent="space-between" color="text.secondary">
        <Typography variant="body2">
          Showing {filteredAndSortedData.length} of {data.length} results
        </Typography>
        {searchTerm && (
          <Typography variant="body2" color="primary">
            Filtered by: "{searchTerm}"
          </Typography>
        )}
      </Box>
    </Box>
    </>
  );
}